import { useState } from 'react';
import { useAccountRequests, useDismissAccountRequest } from '../../../api/admin';
import type { AccountRequest, AccountRequestStatus } from '@spi/shared';
import { Pagination } from '../shared/Pagination';
import { ConfirmDialog } from '../shared/ConfirmDialog';
import { CreateUserModal } from './CreateUserModal';
import toast from 'react-hot-toast';

function parseName(fullName: string): { firstName: string; lastName: string } {
  const parts = fullName.trim().split(/\s+/);
  if (parts.length === 1) return { firstName: parts[0], lastName: '' };
  return { firstName: parts[0], lastName: parts.slice(1).join(' ') };
}

const statusColors: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700',
  dismissed: 'bg-gray-100 text-gray-600',
};

export function AccountRequestsList() {
  const [statusFilter, setStatusFilter] = useState<AccountRequestStatus | ''>('pending');
  const [page, setPage] = useState(1);

  const { data, isLoading } = useAccountRequests({
    status: (statusFilter || undefined) as AccountRequestStatus | undefined,
    page,
    limit: 20,
  });

  const dismiss = useDismissAccountRequest();
  const [confirmDismiss, setConfirmDismiss] = useState<{ id: number; name: string } | null>(null);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createPrefill, setCreatePrefill] = useState<{
    firstName?: string;
    lastName?: string;
    email?: string;
    role?: 'donor' | 'recipient';
    organization?: { name?: string; type?: string; country?: string };
    accountRequestId?: number;
  } | undefined>(undefined);

  const handleCreateFromRequest = (req: AccountRequest) => {
    const { firstName, lastName } = parseName(req.name);
    setCreatePrefill({
      firstName,
      lastName,
      email: req.email,
      role: req.role as 'donor' | 'recipient',
      organization: {
        name: req.organizationName,
        type: req.organizationType,
        country: req.country,
      },
      accountRequestId: req.id,
    });
    setCreateModalOpen(true);
  };

  const handleDismissConfirm = async () => {
    if (!confirmDismiss) return;
    try {
      await dismiss.mutateAsync(confirmDismiss.id);
      toast.success('Application dismissed');
    } catch {
      toast.error('Failed to dismiss application');
    }
    setConfirmDismiss(null);
  };

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value as AccountRequestStatus | ''); setPage(1); }}
          className="input max-w-[180px] text-sm py-2"
        >
          <option value="">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="dismissed">Dismissed</option>
        </select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-spi-green border-t-transparent" />
        </div>
      ) : data?.data.length === 0 ? (
        <p className="text-spi-slate text-sm py-8 text-center">No applications found.</p>
      ) : (
        <>
          <div className="space-y-3">
            {data?.data.map((req) => (
              <div key={req.id} className="card p-5">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-semibold text-spi-charcoal">{req.name}</h4>
                    <p className="text-sm text-spi-slate">{req.email}</p>
                    <p className="text-xs text-spi-slate mt-1">
                      {req.organizationName} ({req.organizationType}) &middot; {req.country}
                    </p>
                    {req.message && (
                      <p className="text-xs text-spi-slate mt-2 line-clamp-2 italic">"{req.message}"</p>
                    )}
                    <p className="text-xs text-spi-slate/60 mt-1">
                      Applied {new Date(req.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${statusColors[req.status] || ''}`}>
                      {req.status}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                      req.role === 'donor' ? 'bg-blue-100 text-blue-700' : 'bg-purple-100 text-purple-700'
                    }`}>
                      {req.role}
                    </span>
                  </div>
                </div>
                {req.status === 'pending' && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    <button
                      onClick={() => handleCreateFromRequest(req)}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-spi-green text-white hover:bg-spi-green-dark transition-colors"
                    >
                      Create Account
                    </button>
                    <button
                      onClick={() => setConfirmDismiss({ id: req.id, name: req.name })}
                      className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200"
                    >
                      Dismiss
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>
          {data && <Pagination page={data.page} totalPages={data.totalPages} onPageChange={setPage} />}
        </>
      )}

      <ConfirmDialog
        open={!!confirmDismiss}
        title="Dismiss Application"
        message={`Are you sure you want to dismiss the application from ${confirmDismiss?.name}?`}
        confirmLabel="Dismiss"
        danger
        onConfirm={handleDismissConfirm}
        onCancel={() => setConfirmDismiss(null)}
      />

      <CreateUserModal
        open={createModalOpen}
        onClose={() => { setCreateModalOpen(false); setCreatePrefill(undefined); }}
        prefill={createPrefill}
      />
    </div>
  );
}
